'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { getImpersonationUserId } from '@/lib/auth-cache';
import { authedFetch } from '@/lib/authed-fetch';

interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  tokenQuota: number | null;
  tokenUsage: number | null;
  createdAt: string;
}

interface OrganizationContextType {
  organizations: Organization[];
  currentOrg: Organization | null;
  loading: boolean;
  error: string | null;
  fetchOrganizations: (force?: boolean) => Promise<Organization[]>;
  setCurrentOrgBySlug: (slug: string) => Organization | null;
  getOrgBySlug: (slug: string) => Organization | null;
  clearCache: () => void;
}

// Default context value for SSR/initial render
const defaultContextValue: OrganizationContextType = {
  organizations: [],
  currentOrg: null,
  loading: true,
  error: null,
  fetchOrganizations: async () => [],
  setCurrentOrgBySlug: () => null,
  getOrgBySlug: () => null,
  clearCache: () => {},
};

const OrganizationContext = createContext<OrganizationContextType>(defaultContextValue);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const queryClient = useQueryClient();
  const supabase = createClient();

  // Track which impersonation state the cache was fetched for
  const cachedForUser = useRef<string | null>(null);

  // Share a single in-flight request across concurrent callers. On a cold dashboard load the
  // sidebar and page component both run their init effect before the first response commits the
  // cache, which would otherwise fire N identical /api/organizations requests.
  const inFlightRef = useRef<Promise<Organization[]> | null>(null);

  const fetchOrganizations = useCallback(
    async (force = false): Promise<Organization[]> => {
      // Check if impersonation state changed since last fetch
      const currentImpersonation = getImpersonationUserId();

      const cacheValid = !force && hasFetched && cachedForUser.current === currentImpersonation;

      // An empty successful response is cached too. Otherwise callers without any
      // workspaces repeatedly fetch as each new empty array updates this callback.
      if (cacheValid) {
        return organizations;
      }

      // Dedupe concurrent callers into the same request. A forced refresh (e.g. after an org
      // rename) bypasses an in-flight read so it can't resolve with pre-mutation data.
      if (inFlightRef.current && !force) {
        return inFlightRef.current;
      }

      const run = async (): Promise<Organization[]> => {
        setLoading(true);
        setError(null);

        try {
          // authedFetch automatically attaches auth token + impersonation header when active
          const response = await authedFetch('/api/organizations');

          const data = await response.json().catch(() => ({}));
          const orgs = data.organizations || [];

          if (!response.ok) {
            const message = data.error || `Failed to fetch organizations (${response.status})`;
            if (response.status === 401) {
              setError('Not authenticated');
              return [];
            }
            setError(message);
            return [];
          }

          setOrganizations(orgs);
          setHasFetched(true);
          cachedForUser.current = currentImpersonation;

          return orgs;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          setError(message);
          console.error('Failed to fetch organizations:', err);
          return [];
        } finally {
          setLoading(false);
        }
      };

      const promise = run();
      inFlightRef.current = promise;
      try {
        return await promise;
      } finally {
        inFlightRef.current = null;
      }
    },
    [hasFetched, organizations]
  );

  const getOrgBySlug = useCallback(
    (slug: string): Organization | null => {
      return organizations.find((org) => org.slug === slug) || null;
    },
    [organizations]
  );

  const setCurrentOrgBySlug = useCallback(
    (slug: string): Organization | null => {
      const org = getOrgBySlug(slug);
      setCurrentOrg(org);
      return org;
    },
    [getOrgBySlug]
  );

  const clearCache = useCallback(() => {
    setOrganizations([]);
    setCurrentOrg(null);
    setHasFetched(false);
    setError(null);
  }, []);

  // Clear cache on auth state change
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        clearCache();
        // Also drop the shared React Query cache so an in-SPA account switch (sign-out
        // paths that stay in the app via router.push, e.g. onboarding/reset-password)
        // can never serve the previous user's cached rows to the next user.
        queryClient.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, [clearCache, supabase.auth, queryClient]);

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrg,
        loading,
        error,
        fetchOrganizations,
        setCurrentOrgBySlug,
        getOrgBySlug,
        clearCache,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

// Internal-only: consumed by useCurrentOrg below. Not part of the public context surface.
function useOrganizations() {
  return useContext(OrganizationContext);
}

/**
 * Hook to get the current organization by slug from URL params.
 * Automatically fetches orgs if not cached and sets current org.
 */
export function useCurrentOrg(orgSlug: string) {
  const {
    organizations,
    currentOrg,
    loading,
    error,
    fetchOrganizations,
    setCurrentOrgBySlug,
    getOrgBySlug,
  } = useOrganizations();

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const init = async () => {
      // If we already have orgs, just set current
      if (organizations.length > 0) {
        setCurrentOrgBySlug(orgSlug);
        setIsInitialized(true);
        return;
      }

      // Fetch orgs and set current
      const orgs = await fetchOrganizations();
      if (orgs.length > 0) {
        setCurrentOrgBySlug(orgSlug);
      }
      setIsInitialized(true);
    };

    init();
  }, [orgSlug, organizations.length, fetchOrganizations, setCurrentOrgBySlug]);

  return {
    // The URL slug is the source of truth: resolve against it synchronously so the first render
    // after an org switch (before the post-commit setCurrentOrgBySlug effect lands) returns the
    // slug-correct org rather than the stale previously-viewed `currentOrg`. Fall back to
    // `currentOrg` only when it already matches the slug (steady state) — on cold load both are
    // null (covered by the loading flag).
    organization: getOrgBySlug(orgSlug) ?? (currentOrg?.slug === orgSlug ? currentOrg : null),
    organizations,
    loading: loading || !isInitialized,
    error,
    // Force-refetch the cached org list (bypassing the in-memory cache) — used after a mutation
    // that changes an org's name/slug so the switcher and current-org lookups don't stay stale.
    refreshOrganizations: () => fetchOrganizations(true),
  };
}
